import React, { useState } from "react";
import {
	AiApprovalDecision,
	AiConversationTurn,
	AiPendingApproval,
} from "../../../models/AiAssistant";
import AiApprovalCard from "./AiApprovalCard";
import AiMessage from "./AiMessage";
import AiPromptInput from "./AiPromptInput";

const TURNS: AiConversationTurn[] = [
	{
		id: "1",
		role: "user",
		text: "Vilka uppdrag har skapats idag?",
		created: new Date().toISOString(),
	},
	{
		id: "2",
		role: "assistant",
		text: [
			"**Tre uppdrag** skapades idag, varav två fortfarande är obemannade. De ligger i tabellen nedan.",
			"",
			"Vill du att jag listar matchande konsulter för de obemannade?",
		].join("\n"),
		created: new Date().toISOString(),
		datasets: [
			{
				id: "ds1",
				toolName: "getJobs",
				title: "Uppdrag",
				columns: ["id", "orderNumber", "customer.name", "startDate", "status", "url"],
				rows: [
					{
						id: "a1",
						orderNumber: 1043,
						"customer.name": "Solrosens förskola",
						startDate: "2026-09-04",
						status: "Obemannat",
						url: "https://oo-dashboard.test-1.fruster.se/jobs/a1",
					},
					{
						id: "a2",
						orderNumber: 1044,
						"customer.name": "Björkskolan",
						startDate: "2026-09-05",
						status: "Bokat",
						url: "https://oo-dashboard.test-1.fruster.se/jobs/a2",
					},
					{
						id: "a3",
						orderNumber: 1045,
						"customer.name": "Vårdcentralen Nord",
						startDate: "2026-09-08",
						status: "Obemannat",
						url: "https://oo-dashboard.test-1.fruster.se/jobs/a3",
					},
				],
			},
		],
		trace: [
			{
				toolName: "getJobs",
				input: { createdFrom: "2026-09-03", pageSize: "50" },
				outputPreview: '{"totalCount":3,"jobs":[{"id":"a1","orderNumber":1043,...}]}',
				durationMs: 412,
				step: 1,
			},
		],
		usage: { inputTokens: 5120, outputTokens: 310, steps: 2 },
		timing: {
			totalMs: 9840,
			modelMs: 9428,
			toolMs: 412,
			steps: [
				{ step: 1, durationMs: 4212, modelMs: 3800, inputTokens: 2100, outputTokens: 60, toolCalls: ["getJobs"] },
				{ step: 2, durationMs: 5628, modelMs: 5628, inputTokens: 3020, outputTokens: 250, toolCalls: [] },
			],
		},
	},
	{
		id: "3",
		role: "user",
		text: "Byt avtal på Björkskolan till Kommunavtal 2026",
		created: new Date().toISOString(),
	},
	{
		id: "4",
		role: "assistant",
		text: "Klart. Björkskolan har nu avtalet **Kommunavtal 2026**.",
		created: new Date().toISOString(),
		approvals: [
			{
				approvalId: "ap1",
				approved: true,
				toolName: "updateCustomer",
				summary: "Uppdatera kund Björkskolan: avtal → Kommunavtal 2026",
			},
		],
		trace: [
			{
				toolName: "getCustomers",
				input: { keyword: "Björkskolan" },
				outputPreview: '{"totalCount":1,"customers":[...]}',
			},
			{
				toolName: "getContracts",
				input: { keyword: "Kommunavtal" },
				outputPreview: '{"totalCount":1,"contracts":[...]}',
			},
			{
				toolName: "updateCustomer",
				input: { customerId: "c1", data: { contract: { id: "k1" } } },
				outputPreview: '{"id":"c1","name":"Björkskolan","contract":{"id":"k1"}}',
			},
		],
		usage: { inputTokens: 9800, outputTokens: 420, steps: 4 },
	},
];

const PENDING: AiPendingApproval[] = [
	{
		approvalId: "p1",
		toolCallId: "tc1",
		toolName: "updateTimeReportStatus",
		input: { entryId: "tr1", label: "Anna Berg 2026-09-01", data: { status: "ATTESTED_BY_ADMIN" } },
		summary: "Attestera tidrapport Anna Berg 2026-09-01 (ATTESTED_BY_ADMIN)",
	},
	{
		approvalId: "p2",
		toolCallId: "tc2",
		toolName: "updateTimeReportStatus",
		input: { entryId: "tr2", label: "Erik Lund 2026-09-01", data: { status: "ATTESTED_BY_ADMIN" } },
		summary: "Attestera tidrapport Erik Lund 2026-09-01 (ATTESTED_BY_ADMIN)",
	},
];

const AiAssistantExample: React.FC = () => {
	const [decisions, setDecisions] = useState<AiApprovalDecision[]>();
	const [sent, setSent] = useState<string[]>([]);

	return (
		<div className="max-w-xl space-y-8 p-6">
			<section>
				<h2 className="mb-3 text-xl font-bold">Thread</h2>
				<div className="space-y-5 rounded-lg border border-border bg-white p-4">
					{TURNS.map((turn) => (
						<AiMessage key={turn.id} turn={turn} />
					))}
				</div>
			</section>

			<section>
				<h2 className="mb-3 text-xl font-bold">Approval card</h2>
				<AiApprovalCard approvals={PENDING} onRespond={setDecisions} />
				{decisions && (
					<pre className="mt-2 rounded bg-neutral-100 p-2 text-xs">
						{JSON.stringify(decisions, null, 2)}
					</pre>
				)}
			</section>

			<section>
				<h2 className="mb-3 text-xl font-bold">Prompt input</h2>
				<AiPromptInput onSend={(text) => setSent((s) => [...s, text])} />
				{sent.length > 0 && (
					<ul className="mt-2 list-disc pl-5 text-sm text-muted-foreground">
						{sent.map((text, index) => (
							<li key={index}>{text}</li>
						))}
					</ul>
				)}
			</section>
		</div>
	);
};

export default AiAssistantExample;
